// ** React Imports
import React from 'react'

// ** Helpers
import { WidgetType } from 'src/lib/types/dashboardAnalytics'
import { Download } from 'react-feather'
import { downloadCsvHelper, arrayToCsv } from 'src/lib/utils/helpers'
import { getAnalyticsWidgetPayload, extractDateFromFilters } from 'src/lib/utils/analyticsHelpers'
import { getAnalyticsWidgetData } from 'src/lib/core/api-methods'
import { analyticsConstants } from 'src/lib/utils/constants'
import { useFilter } from 'src/lib/contexts/FilterContext'
import { errorToast } from 'src/lib/utils/toasters'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import StaticAlert from 'src/components/common/feedback-and-dialog/StaticAlert'
import { EntityName, useActivityLogs } from 'src/lib/hooks/useActivityLogs'

const ACRSurveyDataWidget: React.FC<WidgetType> = ({ id, value, isLoading, customFilters }) => {
  const { analyticsFilters } = useFilter()
  const { setLoaderProperties } = useLoader()
  const { sendActivityLog } = useActivityLogs()

  const ACRSurveyDataCount = value?.[0]?.data
  const ACRSurveyDataPercentage = value?.[1]?.data
  const ACRSurveyTotalCount = value?.[2]?.data

  const handleDownloadClick = async () => {
    setLoaderProperties({ isLoaderVisible: true, message: 'Downloading all responses...' })
    try {
      const response: any = await getAnalyticsWidgetData(
        getAnalyticsWidgetPayload(
          analyticsConstants.FLO_ACR_SURVEY_DATA_ALL,
          extractDateFromFilters(analyticsFilters, 'startDate'),
          extractDateFromFilters(analyticsFilters, 'endDate'),
          undefined,
          undefined,
          customFilters
        )
      )
      downloadCsvHelper(arrayToCsv(response?.data?.widgetData?.[0]?.data, ['Message', 'Count']), `acr_surveys.csv`)
      sendActivityLog({
        entityName: EntityName.OVERVIEW,
        actionName: 'exported data'
      })
    } catch (err) {
      console.error(err)
      errorToast('Failed to download data.')
    } finally {
      setLoaderProperties({ isLoaderVisible: false })
    }
  }

  return (
    <div className='w-full h-full justify-between flex flex-col gap-6'>
      <ul className='w-full flex flex-col gap-2'>
        {!Boolean(ACRSurveyDataCount?.length) && (
          <StaticAlert heading={'No responses are available at the moment'} type='INFO' />
        )}
        {ACRSurveyDataCount?.map((surveyData: any, index: number) => {
          return (
            <li key={`Survey ${index + 1}`} className='˝w-full flex flex-col gap-1'>
              <p className='inline-flex text-sm font-normal text-coal-light'>
                {Boolean(surveyData?.x) ? surveyData?.x : '-'}
              </p>
              <div className='h-5 w-full flex flex-row justify-between'>
                <div className='w-3/4 flex flex-row items-center justify-start'>
                  <div
                    className='bg-[#EFD080] h-full rounded-sm'
                    style={{ width: `${ACRSurveyDataPercentage?.[index]?.y}%` }}
                  />
                </div>
                <div className='inline-flex text-sm font-normal gap-0.5'>
                  <span className='text-gray-dark'>{surveyData?.y}</span>
                  <span className='text-coal-light'>{'·'}</span>
                  <span className='text-carbon'>{ACRSurveyDataPercentage?.[index]?.y}%</span>
                </div>
              </div>
            </li>
          )
        })}
      </ul>
      <div className='flex flex-row w-full justify-between' onClick={handleDownloadClick}>
        <p className='text-sm font-normal text-gray-dark'>
          {Boolean(ACRSurveyTotalCount?.[0]?.total) ? ACRSurveyTotalCount?.[0]?.total + ' responses' : '0 responses'}
        </p>
        <button className='flex flex-row text-coal-dark text-sm font-semibold gap-1.5'>
          <Download className='w-4 h-4' />
          <span>All responses</span>
        </button>
      </div>
    </div>
  )
}

export default ACRSurveyDataWidget
